import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type { Notification } from '../../lib/supabase';

interface RelatedUser {
  id: string;
  username: string;
  avatar_url: string | null;
  avatar_zoom?: {
    x: number;
    y: number;
    width: number;
    height: number;
    scale: number;
  } | null;
}

interface NotificationWithProfile extends Notification {
  related_user?: RelatedUser | null;
  anime_characters?: {
    character_name: string;
    image_url: string;
  };
}

export default function NotificationBell() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationWithProfile[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      
      // Subscribe to new notifications
      const channel = supabase
        .channel('notifications')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            fetchNotifications();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      if (data && data.length > 0) {
        // Get unique user IDs and character IDs
        const userIds = [...new Set(data.map(n => n.related_user_id).filter(Boolean))];
        const characterIds = [...new Set(data.map(n => n.related_character_id).filter(Boolean))];
        
        // Fetch all profiles and characters in parallel
        const [profilesResult, charactersResult] = await Promise.all([
          userIds.length > 0 
            ? supabase
                .from('profiles')
                .select('id, username, avatar_url, avatar_zoom')
                .in('id', userIds)
            : Promise.resolve({ data: [] }),
          characterIds.length > 0
            ? supabase
                .from('anime_characters')
                .select('id, character_name, image_url')
                .in('id', characterIds)
            : Promise.resolve({ data: [] })
        ]);

        // Create lookup maps for faster access
        const profilesMap = new Map(
          (profilesResult.data || []).map(p => [p.id, p])
        );
        const charactersMap = new Map(
          (charactersResult.data || []).map(c => [c.id, c])
        );

        // Merge data efficiently
        const notificationsWithProfiles = data.map(notification => ({
          ...notification,
          related_user: notification.related_user_id 
            ? profilesMap.get(notification.related_user_id) || null 
            : null,
          anime_characters: notification.related_character_id
            ? charactersMap.get(notification.related_character_id) || undefined
            : undefined
        }));

        setNotifications(notificationsWithProfiles as NotificationWithProfile[]);
        setUnreadCount(data.filter(n => !n.read).length);
      } else {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) throw error;

      // Update local state
      setNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) throw error;

      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const clearNotification = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;

      // Update local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      const wasUnread = notifications.find(n => n.id === notificationId)?.read === false;
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error clearing notification:', error);
    }
  };

  const clearAllNotifications = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error clearing all notifications:', error);
    }
  };

  const getAvatarStyle = (relatedUser: RelatedUser | null | undefined) => {
    if (!relatedUser?.avatar_zoom) {
      return {
        objectFit: 'cover' as const,
        objectPosition: 'top center',
      };
    }

    const zoom = relatedUser.avatar_zoom;
    const containerSize = 40; // w-10 = 40px
    
    const cropWidth = zoom.width;
    const scale = containerSize / cropWidth;
    const offsetX = -zoom.x * scale;
    const offsetY = -zoom.y * scale;

    return {
      transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
      transformOrigin: 'top left',
      width: 'auto',
      height: 'auto',
      maxWidth: 'none',
    };
  };

  const getNotificationLink = (notification: NotificationWithProfile) => {
    if (notification.type === 'new_rating' && notification.related_character_id) {
      return `/character/${notification.related_character_id}`;
    }
    if (notification.related_user_id) {
      return `/profile/${notification.related_user_id}`;
    }
    return '#';
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative w-10 h-10 flex items-center justify-center text-gray-700 hover:text-pink-500 transition-colors cursor-pointer"
      >
        <i className="ri-notification-3-line text-2xl"></i>
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div 
          className="absolute mt-2 w-[calc(100vw-2rem)] sm:w-96 max-w-96 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50" 
          style={{ 
            right: window.innerWidth < 640 ? 'auto' : '0',
            left: window.innerWidth < 640 ? 'auto' : 'auto',
            transform: window.innerWidth < 640 ? 'translateX(calc(-100% + 4rem))' : 'none'
          }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-pink-50 to-purple-50">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-pink-600 hover:text-pink-700 font-medium cursor-pointer whitespace-nowrap"
                >
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={clearAllNotifications}
                  className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <i className="ri-notification-off-line text-3xl text-gray-400"></i>
                </div>
                <p className="text-gray-500 text-sm">No notifications yet</p>
              </div>
            ) : (
              <div>
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                      !notification.read ? 'bg-pink-50/30' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Avatar or Character Image */}
                      <Link
                        to={getNotificationLink(notification)}
                        onClick={() => {
                          markAsRead(notification.id);
                          setShowDropdown(false);
                        }}
                        className="flex-shrink-0 cursor-pointer"
                      >
                        {notification.anime_characters?.image_url ? (
                          <img
                            src={notification.anime_characters.image_url}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover object-top"
                          />
                        ) : notification.related_user?.avatar_url ? (
                          <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-pink-500 bg-gray-100">
                            <img
                              src={notification.related_user.avatar_url}
                              alt=""
                              style={getAvatarStyle(notification.related_user)}
                            />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center">
                            <i className="ri-user-line text-white"></i>
                          </div>
                        )}
                      </Link>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <Link
                          to={getNotificationLink(notification)}
                          onClick={() => {
                            markAsRead(notification.id);
                            setShowDropdown(false);
                          }}
                          className="cursor-pointer"
                        >
                          <p className="text-sm text-gray-900 mb-1">
                            {notification.content}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatTimeAgo(notification.created_at)}
                          </p>
                        </Link>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!notification.read && (
                          <button
                            onClick={() => markAsRead(notification.id)}
                            className="w-6 h-6 flex items-center justify-center text-pink-500 hover:text-pink-600 cursor-pointer"
                            title="Mark as read"
                          >
                            <i className="ri-check-line text-lg"></i>
                          </button>
                        )}
                        <button
                          onClick={() => clearNotification(notification.id)}
                          className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                          title="Clear"
                        >
                          <i className="ri-close-line text-lg"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
